import React from 'react';
import { ACABAMENTO_OPTIONS, isWetRoom } from './constants';

/**
 * Right panel: room details + acabamentos + esquadrias.
 * Opens when a room is selected.
 */
export default function RightPanel({
  room,
  roomIndex,
  isOpen,
  onClose,
  onAcabamentoChange,
  onNameChange,
  onAddEsquadria,
  onRemoveEsquadria,
  onEsquadriaMatChange,
}) {
  if (!isOpen || !room) return null;

  const wet = isWetRoom(room.familia || room.fam);
  const acab = room.acab || {};
  const esqs = room.esq || room.esquadrias || [];

  const handleAcab = (field, value) => {
    onAcabamentoChange && onAcabamentoChange(roomIndex, field, value);
  };

  const renderSelect = (field, value) => {
    const options = ACABAMENTO_OPTIONS[field] || [];
    return (
      <select
        value={value || ''}
        onChange={(e) => handleAcab(field, e.target.value)}
        style={{
          width: '100%',
          padding: '6px 8px',
          border: '1px solid #E5E7EB',
          borderRadius: 6,
          fontSize: 12,
          fontFamily: 'Inter, sans-serif',
          color: '#374151',
          background: '#FFFFFF',
          outline: 'none',
        }}
      >
        {options.map(o => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    );
  };

  const sectionStyle = {
    padding: '10px 0',
    borderBottom: '1px solid #F3F4F6',
  };

  const labelStyle = {
    fontSize: 10,
    fontWeight: 700,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 8,
    fontFamily: 'Inter, sans-serif',
  };

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
    fontSize: 12,
    fontFamily: 'Inter, sans-serif',
  };

  const fieldGroupStyle = {
    marginBottom: 8,
  };

  const fieldLabelStyle = {
    fontSize: 11,
    color: '#6B7280',
    marginBottom: 3,
    fontFamily: 'Inter, sans-serif',
  };

  return (
    <div style={{
      width: 280,
      minWidth: 280,
      height: '100%',
      borderLeft: '1px solid #E5E7EB',
      background: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 14px',
        borderBottom: '1px solid #E5E7EB',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          <div style={{
            fontWeight: 700,
            fontSize: 14,
            color: '#1E3A5F',
            fontFamily: 'Inter, sans-serif',
          }}>
            {room.nome}
          </div>
          <div style={{
            fontSize: 11,
            color: '#9CA3AF',
            fontFamily: 'Inter, sans-serif',
          }}>
            {room.area} m² • {room.confianca || 'média'}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 18,
            color: '#9CA3AF',
            padding: 4,
          }}
        >
          ✕
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '0 14px',
      }}>
        {/* Geometry section */}
        <div style={sectionStyle}>
          <div style={labelStyle}>Geometria</div>
          <div style={rowStyle}>
            <span style={{ color: '#6B7280' }}>Área</span>
            <span style={{ fontWeight: 600, color: '#1E3A5F' }}>{room.area} m²</span>
          </div>
          <div style={rowStyle}>
            <span style={{ color: '#6B7280' }}>Dimensões</span>
            <span style={{ fontWeight: 600, color: '#1E3A5F' }}>
              {(() => {
                // Derive real dimensions from area + bbox aspect ratio
                const area = room.area || 0;
                const aw = room.aw || 0;
                const ah = room.ah || 0;
                if (area > 0 && aw > 0 && ah > 0) {
                  const aspect = aw / ah;
                  const wm = Math.sqrt(area * aspect);
                  const hm = area / wm;
                  return `${wm.toFixed(2)} × ${hm.toFixed(2)}m`;
                }
                return '—';
              })()}
            </span>
          </div>
          <div style={rowStyle}>
            <span style={{ color: '#6B7280' }}>Confiança</span>
            <span style={{
              fontWeight: 600,
              color: room.confianca === 'alta' ? '#22C55E' : room.confianca === 'baixa' ? '#EF4444' : '#F59E0B',
            }}>
              {room.confianca || 'média'}
            </span>
          </div>
        </div>

        {/* Edit name */}
        <div style={sectionStyle}>
          <div style={labelStyle}>Editar</div>
          <div style={fieldGroupStyle}>
            <div style={fieldLabelStyle}>Nome</div>
            <input
              value={room.nome || ''}
              onChange={(e) => onNameChange && onNameChange(roomIndex, e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid #E5E7EB',
                borderRadius: 6,
                fontSize: 12,
                fontFamily: 'Inter, sans-serif',
                color: '#374151',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* Acabamentos section */}
        <div style={sectionStyle}>
          <div style={labelStyle}>Acabamentos</div>

          <div style={fieldGroupStyle}>
            <div style={fieldLabelStyle}>Piso</div>
            {renderSelect('piso', acab.piso)}
          </div>

          <div style={fieldGroupStyle}>
            <div style={fieldLabelStyle}>{wet ? 'Revestimento' : 'Parede'}</div>
            {wet
              ? renderSelect('rev', acab.rev)
              : renderSelect('parede', acab.parede)
            }
          </div>

          {!wet && (
            <div style={fieldGroupStyle}>
              <div style={fieldLabelStyle}>Rodapé</div>
              {renderSelect('rodape', acab.rodape)}
            </div>
          )}

          <div style={fieldGroupStyle}>
            <div style={fieldLabelStyle}>Teto / Forro</div>
            {renderSelect('teto', acab.teto)}
          </div>
        </div>

        {/* Esquadrias section */}
        <div style={sectionStyle}>
          <div style={labelStyle}>Esquadrias ({esqs.length})</div>

          {esqs.map((esq, eIdx) => (
            <div key={eIdx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 0',
              borderBottom: '1px solid #F9FAFB',
              fontSize: 11,
              fontFamily: 'Inter, sans-serif',
            }}>
              <span>{esq.t === 'porta' || esq.tipo === 'Porta' ? '🚪' : '🪟'}</span>
              <span style={{ flex: 1, color: '#374151' }}>
                {esq.c || esq.tipo || (esq.t === 'porta' ? 'Porta' : 'Janela')}
              </span>
              <span style={{ color: '#9CA3AF', fontSize: 10 }}>
                {esq.dm || ''}
              </span>
              <select
                value={esq.m || esq.material || ''}
                onChange={(e) => onEsquadriaMatChange && onEsquadriaMatChange(roomIndex, eIdx, e.target.value)}
                style={{
                  width: 80,
                  padding: '2px 4px',
                  border: '1px solid #E5E7EB',
                  borderRadius: 4,
                  fontSize: 10,
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {(esq.t === 'porta' || esq.tipo === 'Porta'
                  ? ACABAMENTO_OPTIONS.mat_porta
                  : ACABAMENTO_OPTIONS.mat_janela
                ).map(o => (
                  <option key={o.v} value={o.v}>{o.l}</option>
                ))}
              </select>
              <button
                onClick={() => onRemoveEsquadria && onRemoveEsquadria(roomIndex, eIdx)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#EF4444',
                  fontSize: 12,
                  padding: 2,
                }}
                title="Excluir"
              >
                ✕
              </button>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button
              onClick={() => onAddEsquadria && onAddEsquadria(roomIndex, 'porta')}
              style={{
                flex: 1,
                padding: '6px 0',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 11,
                fontFamily: 'Inter, sans-serif',
                color: '#374151',
              }}
            >
              + Porta
            </button>
            <button
              onClick={() => onAddEsquadria && onAddEsquadria(roomIndex, 'janela')}
              style={{
                flex: 1,
                padding: '6px 0',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 11,
                fontFamily: 'Inter, sans-serif',
                color: '#374151',
              }}
            >
              + Janela
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
